import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Stack,
  Alert,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { BaseSelectInput } from 'views/App/Portal/PerformanceReporting/_components/FieldInputs';
import LockIcon from '@mui/icons-material/Lock';
import { neutral } from '_lex/_foundations/phxColors';
import { TemplateSection, Template, TemplateField, VisibilityCondition } from '../../_types';
import { getSectionInheritanceType, isSectionLockedByParent } from '../../_utils/sectionComparison';
import { translate as t } from 'hooks/useTranslations';
import { useTemplateBuilderContext } from '../context';
import InheritanceBadge from './InheritanceBadge';
import { filterActiveFieldOptions } from '../../_utils/templateFilters';

const ELIGIBLE_FIELD_TYPES = ['Boolean', 'SingleSelect', 'MultiSelect'] as const;

interface SectionPropertiesPanelProps {
  section: TemplateSection;
  parentTemplate?: Template | null;
  onTitleChange: (sectionId: number, title: string) => void;
  onDescriptionChange: (sectionId: number, description: string) => void;
  onRequiredChange: (sectionId: number, required: boolean) => void;
  onLockedChange: (sectionId: number, locked: boolean) => void;
  onRepeatableChange: (sectionId: number, repeatable: boolean) => void;
  onConditionallyVisibleChange: (sectionId: number, isConditionallyVisible: boolean) => void;
  onVisibilityConditionsChange: (sectionId: number, conditions: VisibilityCondition[]) => void;
  allSections: TemplateSection[];
  visibilityValidationError: boolean;
}

const SectionPropertiesPanel: React.FC<SectionPropertiesPanelProps> = ({
  section,
  parentTemplate,
  onTitleChange,
  onDescriptionChange,
  onRequiredChange,
  onLockedChange,
  onRepeatableChange,
  onConditionallyVisibleChange,
  onVisibilityConditionsChange,
  allSections,
  visibilityValidationError,
}) => {
  const { isLexipolTemplate, template } = useTemplateBuilderContext();
  const templateMode = isLexipolTemplate ? 'lexipol' : 'agency';
  const isChildTemplate = !!template.parentTemplateId;
  const inheritanceType = isChildTemplate ? getSectionInheritanceType(section, template.parentTemplate) : null;
  const [activeTab, setActiveTab] = useState(0);

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onTitleChange(section.id, event.target.value);
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onDescriptionChange(section.id, event.target.value);
  };

  const handleRequiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRequiredChange(section.id, event.target.checked);
  };

  const handleLockedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onLockedChange(section.id, event.target.checked);
  };

  const handleRepeatableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRepeatableChange(section.id, event.target.checked);
  };

  const displayName = section.effectiveName || section.name || '';
  const displayDescription = section.effectiveDescription || section.description || '';
  const isLockedByParent = isSectionLockedByParent(section, parentTemplate);

  const isAlwaysVisible = !section.effectiveIsConditionallyVisible;
  const handleAlwaysVisibleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onConditionallyVisibleChange(section.id, !event.target.checked);
  };

  const availableFields = useMemo(() => {
    const isCurrentSection = (s: TemplateSection) =>
      section.id === 0 ? s.id === 0 && s.sectionGuid === section.sectionGuid : s.id === section.id;
    const otherSections = allSections.filter((s) => !s.isDeleted && !s.effectiveIsDeleted && !isCurrentSection(s));
    const fields: TemplateField[] = [];
    otherSections.forEach((s) => {
      (s.sectionFields || []).forEach((f) => {
        if (
          !f.isDeleted &&
          !f.effectiveIsDeleted &&
          ELIGIBLE_FIELD_TYPES.includes(f.fieldType as (typeof ELIGIBLE_FIELD_TYPES)[number])
        ) {
          fields.push(f);
        }
      });
    });
    return fields;
  }, [allSections, section.id, section.sectionGuid]);

  const hasNoEligibleFields = availableFields.length === 0 && !section.effectiveIsConditionallyVisible;

  const selectedCondition = section.visibilityConditions?.[0] ?? null;
  const selectedField = selectedCondition
    ? availableFields.find((f) => f.id === selectedCondition.sectionFieldId)
    : null;

  const valueOptions = useMemo(() => {
    if (!selectedField) return [];
    if (selectedField.fieldType === 'Boolean') {
      return [
        { label: t('performanceReporting.templates.builder.visibility.booleanTrue'), value: 'true' },
        { label: t('performanceReporting.templates.builder.visibility.booleanFalse'), value: 'false' },
      ];
    }
    if (selectedField.fieldType === 'SingleSelect' || selectedField.fieldType === 'MultiSelect') {
      const options = filterActiveFieldOptions(selectedField.sectionFieldOptions ?? []);
      return options.map((opt) => ({ label: opt.effectiveLabel, value: opt.effectiveLabel }));
    }
    return [];
  }, [selectedField]);

  const handleFieldChange = (value: string | string[]) => {
    const fieldIdStr = Array.isArray(value) ? value[0] : value;
    const fieldId = fieldIdStr ? Number(fieldIdStr) : null;
    if (fieldId) {
      onVisibilityConditionsChange(section.id, [
        { id: selectedCondition?.id ?? 0, sectionFieldId: fieldId, value: '', operator: 'Equals' },
      ]);
    } else {
      onVisibilityConditionsChange(section.id, []);
    }
  };

  const handleValueChange = (value: string | string[]) => {
    const newValue = Array.isArray(value) ? value[0] : value;
    if (selectedCondition) {
      onVisibilityConditionsChange(section.id, [{ ...selectedCondition, value: newValue ?? '' }]);
    }
  };

  const showVisibilityError = visibilityValidationError && section.effectiveIsConditionallyVisible;
  const fieldError = showVisibilityError && !selectedField;
  const valueError = showVisibilityError && !!selectedField && !selectedCondition?.value;

  const generalContent = (
    <Stack spacing={3}>
      {isLockedByParent && (
        <Alert
          severity='warning'
          icon={<LockIcon fontSize='small' />}
          sx={{
            '& .MuiAlert-icon': { color: '#ed6c02' },
            '& .MuiAlert-message': {
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            },
          }}
        >
          {t(`performanceReporting.templates.builder.${templateMode}.lockedSection.banner`)}
        </Alert>
      )}
      {inheritanceType && (
        <Box>
          <InheritanceBadge
            type={inheritanceType}
            entity='section'
            organizationId={template.organizationId}
          />
        </Box>
      )}
      <Box>
        <Typography
          variant='body2'
          fontWeight='600'
          color='text.primary'
          sx={{ mb: 1 }}
        >
          Section title
        </Typography>
        <TextField
          fullWidth
          size='small'
          value={displayName}
          onChange={handleTitleChange}
          variant='outlined'
          disabled={isLockedByParent}
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper', fontSize: '0.875rem' } }}
        />
      </Box>
      <Box>
        <Typography
          variant='body2'
          fontWeight='600'
          color='text.primary'
          sx={{ mb: 1 }}
        >
          Section description
        </Typography>
        <TextField
          fullWidth
          size='small'
          multiline
          rows={3}
          value={displayDescription}
          onChange={handleDescriptionChange}
          variant='outlined'
          placeholder='This is an example of a description'
          disabled={isLockedByParent}
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper', fontSize: '0.875rem' } }}
        />
        <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              hyphens: 'auto',
              whiteSpace: 'normal',
              display: 'block',
              lineHeight: 1.4,
            }}
          >
            Will be displayed below the section title to help users understand the purpose of this section.
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box>
        <Tooltip
          title={t(`performanceReporting.templates.builder.${templateMode}.lockedSection.inheritedAttribute`)}
          disableHoverListener={!isLockedByParent}
          placement='top'
        >
          <FormControlLabel
            control={
              <Switch
                checked={section.effectiveIsRequired || false}
                onChange={handleRequiredChange}
                size='small'
                disabled={isLockedByParent}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
                }}
              />
            }
            label={
              <Box>
                <Typography
                  variant='body2'
                  fontWeight='600'
                  color='text.primary'
                >
                  Required section
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto' }}
                >
                  Marks the section as required in templates.
                </Typography>
              </Box>
            }
            labelPlacement='start'
            sx={{
              m: 0,
              width: '100%',
              justifyContent: 'space-between',
              gap: 2,
              '& .MuiFormControlLabel-label': {
                flex: 1,
                maxWidth: 'calc(100% - 80px)',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                marginRight: 2,
              },
            }}
          />
        </Tooltip>
      </Box>
      {isLexipolTemplate && (
        <Box>
          <Tooltip
            title={t(`performanceReporting.templates.builder.${templateMode}.lockedSection.inheritedAttribute`)}
            disableHoverListener={!isLockedByParent}
            placement='top'
          >
            <FormControlLabel
              control={
                <Switch
                  checked={section.effectiveIsLocked || false}
                  onChange={handleLockedChange}
                  size='small'
                  disabled={isLockedByParent}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
                  }}
                />
              }
              label={
                <Box>
                  <Typography
                    variant='body2'
                    fontWeight='600'
                    color='text.primary'
                  >
                    Locked section
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto' }}
                  >
                    Locks section and fields from agency edits.
                  </Typography>
                </Box>
              }
              labelPlacement='start'
              sx={{
                m: 0,
                width: '100%',
                justifyContent: 'space-between',
                gap: 2,
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                  maxWidth: 'calc(100% - 80px)',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  marginRight: 2,
                },
              }}
            />
          </Tooltip>
        </Box>
      )}
      <Box>
        <Tooltip
          title={t(`performanceReporting.templates.builder.${templateMode}.lockedSection.inheritedAttribute`)}
          disableHoverListener={!isLockedByParent}
          placement='top'
        >
          <FormControlLabel
            control={
              <Switch
                checked={section.effectiveIsRepeatable || false}
                onChange={handleRepeatableChange}
                size='small'
                disabled={isLockedByParent}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
                }}
              />
            }
            label={
              <Box>
                <Typography
                  variant='body2'
                  fontWeight='600'
                  color='text.primary'
                >
                  Repeatable section
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto' }}
                >
                  Section can be repeated during data entry.
                </Typography>
              </Box>
            }
            labelPlacement='start'
            sx={{
              m: 0,
              width: '100%',
              justifyContent: 'space-between',
              gap: 2,
              '& .MuiFormControlLabel-label': {
                flex: 1,
                maxWidth: 'calc(100% - 80px)',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                marginRight: 2,
              },
            }}
          />
        </Tooltip>
      </Box>
    </Stack>
  );

  const visibilityContent = (
    <Stack spacing={6}>
      {isLockedByParent && (
        <Alert
          severity='warning'
          icon={<LockIcon fontSize='small' />}
          sx={{
            '& .MuiAlert-icon': { color: '#ed6c02' },
            '& .MuiAlert-message': {
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            },
          }}
        >
          {t(`performanceReporting.templates.builder.${templateMode}.lockedSection.banner`)}
        </Alert>
      )}
      {inheritanceType && (
        <Box>
          <InheritanceBadge
            type={inheritanceType}
            entity='section'
            organizationId={template.organizationId}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant='body2'
            fontWeight='600'
            color='text.primary'
          >
            {t('performanceReporting.templates.builder.visibility.sectionVisibility')}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {isAlwaysVisible
              ? t('performanceReporting.templates.builder.visibility.alwaysVisible')
              : t('performanceReporting.templates.builder.visibility.conditionallyVisible')}
          </Typography>
        </Box>
        <Tooltip
          title={
            hasNoEligibleFields
              ? t('performanceReporting.templates.builder.visibility.noEligibleFields')
              : t(`performanceReporting.templates.builder.${templateMode}.lockedSection.inheritedAttribute`)
          }
          disableHoverListener={!isLockedByParent && !hasNoEligibleFields}
          placement='top'
        >
          <span>
            <Switch
              checked={isAlwaysVisible}
              onChange={handleAlwaysVisibleChange}
              size='small'
              disabled={isLockedByParent || hasNoEligibleFields}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
              }}
            />
          </span>
        </Tooltip>
      </Box>
      {section.effectiveIsConditionallyVisible && (
        <>
          <Divider />
          <Box sx={{ backgroundColor: neutral[50], borderRadius: 2, p: 6 }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant='body2'
                  fontWeight='600'
                  color='text.primary'
                  sx={{ mb: 0.5 }}
                >
                  {t('performanceReporting.templates.builder.visibility.fieldLabel')} *
                </Typography>
                <BaseSelectInput
                  hideLabel
                  options={availableFields.map((f) => ({
                    value: String(f.id),
                    label: f.effectiveLabel ?? f.name ?? '',
                  }))}
                  value={selectedField ? String(selectedField.id) : ''}
                  onChange={handleFieldChange}
                  placeholder={t('performanceReporting.templates.builder.visibility.fieldPlaceholder')}
                  error={fieldError ? t('performanceReporting.templates.builder.visibility.fieldRequired') : undefined}
                  disabled={isLockedByParent}
                  isClearable
                  isSearchable
                />
              </Box>
              <Box>
                <Typography
                  variant='body2'
                  fontWeight='600'
                  color='text.primary'
                  sx={{ mb: 0.5 }}
                >
                  {t('performanceReporting.templates.builder.visibility.valueLabel')} *
                </Typography>
                <BaseSelectInput
                  hideLabel
                  options={valueOptions}
                  value={selectedCondition?.value ?? ''}
                  onChange={handleValueChange}
                  placeholder={t('performanceReporting.templates.builder.visibility.valuePlaceholder')}
                  error={valueError ? t('performanceReporting.templates.builder.visibility.fieldRequired') : undefined}
                  disabled={isLockedByParent || !selectedField}
                  isClearable
                />
              </Box>
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );

  return (
    <Box sx={{ height: '100%', overflow: 'auto', maxWidth: '100%' }}>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant='fullWidth'
        sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}
      >
        <Tab
          label={t('performanceReporting.templates.builder.visibility.tabGeneral')}
          id='section-tab-general'
          aria-controls='section-panel-general'
        />
        <Tab
          label={t('performanceReporting.templates.builder.visibility.tabVisibility')}
          id='section-tab-visibility'
          aria-controls='section-panel-visibility'
        />
      </Tabs>
      <Box
        role='tabpanel'
        id='section-panel-general'
        aria-labelledby='section-tab-general'
        hidden={activeTab !== 0}
        sx={{ p: 2 }}
      >
        {activeTab === 0 && generalContent}
      </Box>
      <Box
        role='tabpanel'
        id='section-panel-visibility'
        aria-labelledby='section-tab-visibility'
        hidden={activeTab !== 1}
        sx={{ p: 2, pt: 3 }}
      >
        {activeTab === 1 && visibilityContent}
      </Box>
    </Box>
  );
};

export default SectionPropertiesPanel;
